/** Build JSON body for POST/PUT pricing rule API from local form state. */

import {
  masterApiErrorMessage,
  translateAggregateBackendMessage,
} from '../../../main/utils/masterApiError';

function trimOrEmpty(v) {
  return v == null ? '' : String(v).trim();
}

function decimalOrNull(v) {
  const s = trimOrEmpty(v);
  return s === '' ? null : s;
}

function intOrDefault(v, def) {
  const n = parseInt(String(v).trim(), 10);
  return Number.isFinite(n) ? n : def;
}

/** API ISO instant → `YYYY-MM-DDTHH:mm` for datetime-local inputs (local timezone). */
export function isoToDatetimeLocalValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
}

/** datetime-local field value → API ISO instant, or null when empty/invalid. */
function datetimeLocalToIso(raw) {
  const s = trimOrEmpty(raw);
  if (!s) return null;
  const d = new Date(s);
  if (!Number.isFinite(d.getTime())) return null;
  return d.toISOString();
}

export function buildPricingRulePayload(form) {
  const payload = {
    code: trimOrEmpty(form.code),
    name: trimOrEmpty(form.name),
    name_ar: trimOrEmpty(form.name_ar),
    description: trimOrEmpty(form.description),
    description_ar: trimOrEmpty(form.description_ar),
    scope: form.scope || 'base',
    // WhatsApp menu lives on PricingPlan; bands stay out of the customer menu.
    show_in_menu: false,
    menu_label_ar: trimOrEmpty(form.menu_label_ar),
    menu_label_en: trimOrEmpty(form.menu_label_en),
    menu_order: intOrDefault(form.menu_order, 0),
    customer_group: trimOrEmpty(form.customer_group),
    tariff_code: trimOrEmpty(form.tariff_code),
    category_code: trimOrEmpty(form.category_code),
    applies_to_group: Boolean(form.applies_to_group),
    // Empty min/max → null (backend treats null min as 0, null max as unlimited)
    cbm_from: decimalOrNull(form.cbm_from),
    cbm_to: decimalOrNull(form.cbm_to),
    weight_from: decimalOrNull(form.weight_from),
    weight_to: decimalOrNull(form.weight_to),
    effective_from: datetimeLocalToIso(form.effective_from),
    effective_to: datetimeLocalToIso(form.effective_to),
    strategy: form.strategy || 'flat_per_shipment',
    total_price: decimalOrNull(form.total_price),
    unit_price: decimalOrNull(form.unit_price),
    ceil_step: decimalOrNull(form.ceil_step),
    percent: decimalOrNull(form.percent),
    min_line_price: decimalOrNull(form.min_line_price),
    threshold_cbm: decimalOrNull(form.threshold_cbm),
    priority: intOrDefault(form.priority, 100),
    active: Boolean(form.active),
    notes: trimOrEmpty(form.notes),
  };

  payload.plan =
    form.plan != null && typeof form.plan === 'object' && form.plan.id != null
      ? form.plan.id
      : form.plan != null && form.plan !== ''
        ? Number(form.plan)
        : null;
  if (!Number.isFinite(payload.plan)) payload.plan = null;

  payload.customer =
    form.customer != null && typeof form.customer === 'object' && form.customer.id != null
      ? form.customer.id
      : form.customer != null && form.customer !== ''
        ? Number(form.customer)
        : null;
  if (!Number.isFinite(payload.customer)) payload.customer = null;

  payload.shipment_type =
    form.shipment_type != null &&
    typeof form.shipment_type === 'object' &&
    form.shipment_type.id != null
      ? form.shipment_type.id
      : form.shipment_type != null && form.shipment_type !== ''
        ? Number(form.shipment_type)
        : null;
  if (!Number.isFinite(payload.shipment_type)) payload.shipment_type = null;

  payload.shipment_unit =
    form.shipment_unit != null &&
    typeof form.shipment_unit === 'object' &&
    form.shipment_unit.id != null
      ? form.shipment_unit.id
      : form.shipment_unit != null && form.shipment_unit !== ''
        ? Number(form.shipment_unit)
        : null;
  if (!Number.isFinite(payload.shipment_unit)) payload.shipment_unit = null;

  payload.settlement_group =
    form.settlement_group != null &&
    typeof form.settlement_group === 'object' &&
    form.settlement_group.id != null
      ? form.settlement_group.id
      : form.settlement_group != null && form.settlement_group !== ''
        ? Number(form.settlement_group)
        : null;
  if (!Number.isFinite(payload.settlement_group)) payload.settlement_group = null;

  return payload;
}

export function pricingRuleFromApi(row) {
  if (!row || typeof row !== 'object') return {};
  return {
    code: row.code ?? '',
    name: row.name ?? '',
    name_ar: row.name_ar ?? '',
    description: row.description ?? '',
    description_ar: row.description_ar ?? '',
    scope: row.scope ?? 'base',
    show_in_menu: Boolean(row.show_in_menu),
    menu_label_ar: row.menu_label_ar ?? '',
    menu_label_en: row.menu_label_en ?? '',
    menu_order: row.menu_order ?? 0,
    plan:
      typeof row.plan === 'object' && row.plan !== null
        ? row.plan
        : row.plan != null
          ? {
              id: row.plan,
              code: row.plan_code ?? null,
              name: row.plan_name ?? null,
            }
          : null,
    customer_group: row.customer_group ?? '',
    tariff_code: row.tariff_code ?? '',
    category_code: row.category_code ?? '',
    applies_to_group: Boolean(row.applies_to_group),
    cbm_from: row.cbm_from != null ? String(row.cbm_from) : '',
    cbm_to: row.cbm_to != null ? String(row.cbm_to) : '',
    weight_from: row.weight_from != null ? String(row.weight_from) : '',
    weight_to: row.weight_to != null ? String(row.weight_to) : '',
    effective_from: isoToDatetimeLocalValue(row.effective_from),
    effective_to: isoToDatetimeLocalValue(row.effective_to),
    strategy: row.strategy ?? 'flat_per_shipment',
    total_price: row.total_price != null ? String(row.total_price) : '',
    unit_price: row.unit_price != null ? String(row.unit_price) : '',
    ceil_step: row.ceil_step != null ? String(row.ceil_step) : '',
    percent: row.percent != null ? String(row.percent) : '',
    min_line_price: row.min_line_price != null ? String(row.min_line_price) : '0',
    threshold_cbm: row.threshold_cbm != null ? String(row.threshold_cbm) : '',
    priority: row.priority ?? 100,
    active: row.active !== false,
    notes: row.notes ?? '',
    customer:
      typeof row.customer === 'object' && row.customer !== null
        ? row.customer
        : row.customer != null
          ? { id: row.customer }
          : null,
    shipment_type:
      typeof row.shipment_type === 'object' && row.shipment_type !== null
        ? row.shipment_type
        : row.shipment_type != null
          ? { id: row.shipment_type }
          : null,
    shipment_unit:
      typeof row.shipment_unit === 'object' && row.shipment_unit !== null
        ? row.shipment_unit
        : row.shipment_unit != null
          ? {
              id: row.shipment_unit,
              serial_number: row.shipment_unit_serial_number ?? null,
            }
          : null,
    shipment_unit_serial_number: row.shipment_unit_serial_number ?? null,
    settlement_group:
      typeof row.settlement_group === 'object' && row.settlement_group !== null
        ? row.settlement_group
        : row.settlement_group != null
          ? {
              id: row.settlement_group,
              code: row.settlement_group_code ?? null,
              name: row.settlement_group_name ?? null,
            }
          : null,
    settlement_group_code: row.settlement_group_code ?? null,
    settlement_group_name: row.settlement_group_name ?? null,
  };
}

const CODE_MAX_LEN = 120;
const NAME_MAX_LEN = 200;

/** Non-empty trimmed string that must parse as a finite number (API decimal fields). */
function optionalDecimalField(raw) {
  const s = trimOrEmpty(raw);
  if (s === '') return { ok: true, empty: true, value: null };
  const n = Number(s);
  if (!Number.isFinite(n)) return { ok: false, empty: false, value: null };
  return { ok: true, empty: false, value: n };
}

function optionalEffectiveMs(raw) {
  const s = trimOrEmpty(raw);
  if (s === '') return { ok: true, empty: true, ms: null };
  const ms = Date.parse(s);
  if (!Number.isFinite(ms)) return { ok: false, empty: false, ms: null };
  return { ok: true, empty: false, ms };
}

/**
 * Client-side guardrails aligned with Unified Pricing migration guide §7 and Appendix A.00.
 * API-required: `code`, `name` only; other checks are strategy/range hygiene.
 * @param {object} form
 * @param {function} t — `useTranslation('ui')` t()
 * @param {{ isCreate?: boolean }} [opts]
 */
export function validatePricingRuleClient(form, t, opts = {}) {
  const { isCreate = false } = opts;
  const errors = {};
  const code = trimOrEmpty(form.code);
  if (!code) {
    errors.code = t('pricingRules.validationCodeRequired');
  } else if (code.length > CODE_MAX_LEN) {
    errors.code = t('pricingRules.validationCodeMaxLength', { max: CODE_MAX_LEN });
  } else if (isCreate && !/^[a-z0-9_]+$/.test(code)) {
    errors.code = t('pricingRules.validationCodeSlug');
  }

  const name = trimOrEmpty(form.name);
  if (!name) {
    errors.name = t('pricingRules.validationNameRequired');
  } else if (name.length > NAME_MAX_LEN) {
    errors.name = t('pricingRules.validationNameMaxLength', { max: NAME_MAX_LEN });
  }

  const sgId =
    form.settlement_group != null &&
    typeof form.settlement_group === 'object' &&
    form.settlement_group.id != null
      ? Number(form.settlement_group.id)
      : form.settlement_group != null && form.settlement_group !== ''
        ? Number(form.settlement_group)
        : NaN;
  if (!Number.isFinite(sgId)) {
    errors.settlement_group = t('pricingRules.validationSettlementGroupRequired');
  }

  const planId =
    form.plan != null && typeof form.plan === 'object' && form.plan.id != null
      ? Number(form.plan.id)
      : form.plan != null && form.plan !== ''
        ? Number(form.plan)
        : NaN;
  if (!Number.isFinite(planId)) {
    errors.plan = t('pricingRules.validationPlanRequired');
  }

  const wfDec = optionalDecimalField(form.weight_from);
  const wtDec = optionalDecimalField(form.weight_to);
  if (!wfDec.ok) errors.weight_from = t('validation.invalidNumber');
  if (!wtDec.ok) errors.weight_to = t('validation.invalidNumber');
  const wf = wfDec.ok && !wfDec.empty ? decimalOrNull(form.weight_from) : null;
  const wt = wtDec.ok && !wtDec.empty ? decimalOrNull(form.weight_to) : null;
  if (wf != null && wt != null && Number(wf) > Number(wt)) {
    errors.weight = t('pricingRules.validationWeightRange');
  }

  const cfDec = optionalDecimalField(form.cbm_from);
  const ctDec = optionalDecimalField(form.cbm_to);
  if (!cfDec.ok) errors.cbm_from = t('validation.invalidNumber');
  if (!ctDec.ok) errors.cbm_to = t('validation.invalidNumber');
  const cf = cfDec.ok && !cfDec.empty ? decimalOrNull(form.cbm_from) : null;
  const ct = cfDec.ok && !ctDec.empty ? decimalOrNull(form.cbm_to) : null;
  if (cf != null && ct != null && Number(cf) > Number(ct)) {
    errors.cbm = t('pricingRules.validationCbmRange');
  }

  const efEff = optionalEffectiveMs(form.effective_from);
  const etEff = optionalEffectiveMs(form.effective_to);
  if (!efEff.ok) errors.effective_from = t('pricingRules.validationEffectiveInvalid');
  if (!etEff.ok) errors.effective_to = t('pricingRules.validationEffectiveInvalid');
  if (
    efEff.ok &&
    etEff.ok &&
    !efEff.empty &&
    !etEff.empty &&
    efEff.ms > etEff.ms
  ) {
    errors.effective = t('pricingRules.validationEffectiveRange');
  }

  const strat = form.strategy || 'flat_per_shipment';

  const minDec = optionalDecimalField(form.min_line_price);
  if (!minDec.ok) {
    errors.min_line_price = t('validation.invalidNumber');
  } else if (minDec.empty) {
    errors.min_line_price = t('pricingRules.validationMinLineRequired');
  } else if (minDec.value < 0) {
    errors.min_line_price = t('pricingRules.validationMinLineNonNeg');
  }

  const threshDec = optionalDecimalField(form.threshold_cbm);
  if (!threshDec.ok) errors.threshold_cbm = t('validation.invalidNumber');

  if (strat === 'meter_per_line_ceil') {
    const step = decimalOrNull(form.ceil_step);
    const stepDec = optionalDecimalField(form.ceil_step);
    if (!stepDec.ok) {
      errors.ceil_step = t('validation.invalidNumber');
    } else if (step == null || Number(step) <= 0) {
      errors.ceil_step = t('pricingRules.validationCeilStep');
    }
  }

  if (strat === 'group_total_lookup') {
    const tpDec = optionalDecimalField(form.total_price);
    if (!tpDec.ok) {
      errors.total_price = t('validation.invalidNumber');
    } else {
      const tp = decimalOrNull(form.total_price);
      if (tp == null || Number(tp) <= 0) {
        errors.total_price = t('pricingRules.validationGroupTotalPrice');
      }
    }
  }

  if (strat === 'percent_addon') {
    const pDec = optionalDecimalField(form.percent);
    if (!pDec.ok) {
      errors.percent = t('validation.invalidNumber');
    } else {
      const p = decimalOrNull(form.percent);
      if (p == null || Number(p) <= 0 || Number(p) > 100) {
        errors.percent = t('pricingRules.validationPercent');
      }
    }
  }

  if (strat === 'flat_per_shipment' || strat === 'flat_addon') {
    const tpDec = optionalDecimalField(form.total_price);
    if (!tpDec.ok) {
      errors.total_price = t('validation.invalidNumber');
    } else if (tpDec.empty) {
      errors.total_price = t('pricingRules.validationFlatTotalRequired');
    } else if (tpDec.value < 0) {
      errors.total_price = t('pricingRules.validationTotalNonNeg');
    }
  }

  if (strat === 'per_unit_weight' || strat === 'meter_per_line' || strat === 'meter_per_line_ceil') {
    const upDec = optionalDecimalField(form.unit_price);
    if (!upDec.ok) {
      errors.unit_price = t('validation.invalidNumber');
    } else if (upDec.empty || upDec.value <= 0) {
      errors.unit_price = t('pricingRules.validationUnitPriceRequired');
    }
  }

  return errors;
}

const PRICING_RULE_API_TOPLEVEL_KEYS = new Set([
  'detail',
  'non_field_errors',
  'message_key',
  'message',
  'error',
]);

function joinDrfMessages(val) {
  if (val == null) return '';
  if (typeof val === 'string') return val.trim();
  if (Array.isArray(val)) {
    return val
      .map((item) => {
        if (item == null) return '';
        if (typeof item === 'string') return item;
        if (typeof item === 'object' && typeof item.message === 'string') return item.message;
        return '';
      })
      .filter(Boolean)
      .join(' ');
  }
  return '';
}

/**
 * Maps DRF-style validation payloads (e.g. `{ code: ["A pricing rule with this code already exists."] }`)
 * to form field messages. Uses generic banner only when there are no field-level messages.
 */
export function pricingRuleApiValidationFeedback(err, t) {
  const fieldErrors = {};
  let banner = '';

  const data = err && err.response && err.response.data;
  if (data && typeof data === 'object') {
    if (Array.isArray(data.non_field_errors) && data.non_field_errors.length) {
      banner = translateAggregateBackendMessage(
        data.non_field_errors.map(String).join('; '),
        t
      );
    }

    Object.keys(data).forEach((key) => {
      if (PRICING_RULE_API_TOPLEVEL_KEYS.has(key)) return;
      const joined = joinDrfMessages(data[key]);
      if (joined) {
        fieldErrors[key] = translateAggregateBackendMessage(joined, t);
      }
    });
  }

  if (!banner && Object.keys(fieldErrors).length === 0) {
    banner = masterApiErrorMessage(err, t);
  }

  return { fieldErrors, saveError: banner };
}
